import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type EdgeTypes,
  type Edge as RFEdge,
  type Node as RFNode,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FileBox, Loader2, X } from 'lucide-react';
import { CustomEdge } from '../components/flow/edges';
import { GenerateNode } from '../components/flow/nodes';
import { DetailPageNode } from '../components/flow/nodes/DetailPageNode';
import GroupNode from '../components/flow/nodes/GroupNode';
import { MiniMapToolbar } from '../components/flow/MiniMapToolbar';
import { NodeErrorBoundary } from '../components/ui/ErrorBoundary';
import type { FeaturedPreviewItem } from '../config/featuredTemplates';
import type { WorkflowSnapshot } from '../stores/worksStore';
import { projectApi } from '../services/projectApi';
import { useAuthStore } from '../stores/authStore';
import { useCanvasStore } from '../stores/canvasStore';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import type { AppNode } from '../types/node';
// NodeType 为枚举值（types/node 仅类型再导出），作值使用须从 enums 导入
import { NodeType } from '../types/enums';
import { isGroupNode } from '../types';
import { toast } from 'sonner';

/* ============ 真实节点渲染（与编辑器 FlowEditor 完全同套组件，不做第二套卡片） ============ */

/** 只读环境全部节点交互回调为 no-op：ReactFlow 层已禁拖拽/连线/选中，组件内按钮不触发业务 */
const noop = () => {};
/* onAddNode 签名要求返回新节点 id（string | undefined），只读画布恒返回 undefined */
const noopId = () => undefined;

/**
 * 只读节点 wrapper：与编辑器 NodeWithZoom 同构（ReactFlow data 装整个 AppNode），
 * 渲染编辑器同一批节点组件（GenerateNode / DetailPageNode），仅回调槽位换 no-op；
 * 节点内媒体播放/图片预览保持可用（与实际节点一致的展示形态）
 */
const ReadOnlyNode = (props: Record<string, unknown>) => {
  const { id, data } = props;
  const node = data as AppNode;

  if (node.type === NodeType.DETAIL_PAGE) {
    return (
      <NodeErrorBoundary nodeId={String(id)} nodeName={node.title}>
        <DetailPageNode
          data={node}
          selected={false}
          dragging={false}
          onNodeUpdate={noop}
          onAddNode={noopId}
          onNodeAction={noop}
        />
      </NodeErrorBoundary>
    );
  }

  return (
    <NodeErrorBoundary nodeId={String(id)} nodeName={node.title}>
      <GenerateNode
        data={node}
        selected={false}
        dragging={false}
        onNodeAction={noop}
        onNodeUpdate={noop}
        onNodeDelete={noop}
        onExtractImage={noop}
        onAddNode={noopId}
        onGeneratePanorama={noop}
        onGenerateMultiAngle={noop}
        onGenerateLighting={noop}
        onToggleChat={noop}
        onOpenChat={noop}
        onOpenDirector={noop}
        onOpenLayerEdit={noop}
        onOpenScript={noop}
        onOpenSmartEdit={noop}
      />
    </NodeErrorBoundary>
  );
};

// nodeTypes 模块级化（对齐 FlowEditor P0-2：对象身份稳定，避免热更新重注册告警）
const readOnlyNodeTypes = {
  [NodeType.PROMPT_INPUT]: ReadOnlyNode,
  [NodeType.MEDIA_UPLOAD]: ReadOnlyNode,
  [NodeType.IMAGE_GENERATOR]: ReadOnlyNode,
  [NodeType.VIDEO_GENERATOR]: ReadOnlyNode,
  [NodeType.AUDIO_GENERATOR]: ReadOnlyNode,
  [NodeType.IMAGE_EDITOR]: ReadOnlyNode,
  [NodeType.PANORAMA_GENERATOR]: ReadOnlyNode,
  [NodeType.DIRECTOR]: ReadOnlyNode,
  [NodeType.LAYER_EDIT]: ReadOnlyNode,
  [NodeType.SCRIPT_NODE]: ReadOnlyNode,
  [NodeType.DETAIL_PAGE]: ReadOnlyNode,
  [NodeType.VIDEO_COMPOSE]: ReadOnlyNode,
  [NodeType.GROUP]: GroupNode,
} as NodeTypes;

const readOnlyEdgeTypes: EdgeTypes = { custom: CustomEdge };

const connectionEdgeId = (c: { from: string; to: string; targetHandle?: string }) =>
  `${c.from}-${c.to}${c.targetHandle ? `-${c.targetHandle}` : ''}`;

/** 与编辑器 buildFlowNodes 同构的只读转换（无选中/measured 复用需求，静态一次转换）：
    父子相对坐标、GROUP data 携带 nodeWidth/nodeHeight、width/height 透传 */
function buildReadOnlyFlowNodes(appNodes: AppNode[]): RFNode[] {
  const nodeIds = new Set(appNodes.map((n) => n.id));
  const nodeMap = new Map(appNodes.map((n) => [n.id, n]));
  return appNodes.map((n) => {
    const hasValidParent = !!(n.parentNode && nodeIds.has(n.parentNode));
    let posX = n.x;
    let posY = n.y;
    if (hasValidParent) {
      const parent = nodeMap.get(n.parentNode!);
      if (parent) {
        posX = n.x - parent.x;
        posY = n.y - parent.y;
      }
    }
    return {
      id: n.id,
      type: n.type,
      position: { x: posX, y: posY },
      data: (isGroupNode(n)
        ? { ...n.data, nodeWidth: n.width, nodeHeight: n.height }
        : n) as unknown as Record<string, unknown>,
      ...(n.width && n.height ? { width: n.width, height: n.height } : {}),
      ...(hasValidParent ? { parentId: n.parentNode! } : {}),
      ...(hasValidParent && n.extent ? { extent: n.extent } : {}),
      ...(hasValidParent && n.expandParent ? { expandParent: n.expandParent } : {}),
    } as RFNode;
  });
}

/** 只读画布（须在 ReactFlowProvider 内）：fitView 满铺 + 视口同步 canvasStore（MiniMapToolbar 缩放百分比数据源） */
const ReadOnlyCanvas: React.FC<{ workflow: WorkflowSnapshot; suppressCleanupRef: { current: boolean } }> = ({ workflow, suppressCleanupRef }) => {
  const { getViewport } = useReactFlow();

  const rfNodes = useMemo<RFNode[]>(() => buildReadOnlyFlowNodes(workflow.nodes), [workflow]);

  const rfEdges = useMemo<RFEdge[]>(
    () =>
      workflow.connections.map((c) => ({
        id: connectionEdgeId(c),
        source: c.from,
        target: c.to,
        targetHandle: c.targetHandle,
        type: 'custom',
      })),
    [workflow]
  );

  /* 快照注入真实 nodeStore：GenerateNode 的输入资产/连线展示 hooks 与编辑器同源读取，
     节点渲染形态与编辑器画布完全一致；卸载时清空（进编辑器时 onLoadWorkflow 会重新加载项目数据，
     不污染编辑器状态）。挂载即复位视口（清掉编辑器残留缩放，MiniMapToolbar 百分比从默认值起算），
     fitView 完成后双帧同步实际视口到 canvasStore */
  useEffect(() => {
    const store = useNodeStore.getState();
    store.setNodes(workflow.nodes);
    store.setConnections(workflow.connections);
    useCanvasStore.getState().resetViewport();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const v = getViewport();
        useCanvasStore.getState().setViewport(v.x, v.y, v.zoom);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      /* 复制项目成功后卸载时，编辑器已通过 onLoadWorkflow 装入项目节点，跳过清空以免抹掉编辑器画布 */
      if (suppressCleanupRef.current) return;
      const cleanup = useNodeStore.getState();
      cleanup.setNodes([]);
      cleanup.setConnections([]);
      useCanvasStore.getState().resetViewport();
    };
  }, [workflow, getViewport]);

  return (
    <ReactFlow
      nodes={rfNodes}
      edges={rfEdges}
      nodeTypes={readOnlyNodeTypes}
      edgeTypes={readOnlyEdgeTypes}
      colorMode="dark"
      fitView
      fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
      minZoom={0.1}
      maxZoom={3}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      edgesFocusable={false}
      panOnDrag
      proOptions={{ hideAttribution: true }}
      onMoveEnd={(_, viewport) => useCanvasStore.getState().setViewport(viewport.x, viewport.y, viewport.zoom)}
    >
      <Background color="rgba(200,200,200,0.2)" gap={20} size={2.5} />
      {/* 左下角缩放控件：与编辑器同一组件（fitView / 缩小 / 百分比 / 放大 / 小地图） */}
      <Panel position="bottom-left" className="!bottom-4 !left-4">
        <MiniMapToolbar />
      </Panel>
    </ReactFlow>
  );
};

interface WorkflowPreviewPageProps {
  /** 当前查看的精选作品（制作流程数据源 = item.workflowData 发布时源项目画布快照） */
  item: FeaturedPreviewItem;
  /** 点击右上角 X：关闭只读画布回到预览页 */
  onClose: () => void;
  /** 复制项目成功后携新项目 id 进入编辑器（由 AppNew 传入 handleSelectWorkflow：含登录门禁） */
  onCopyProject?: (projectId: string) => void | Promise<void>;
}

/**
 * 只读画布项目页（查看制作流程，对齐即梦 work-detail 布局）：
 * - 全屏覆盖层（z-[170] > 预览页 z-[160]）：深色画布满铺源项目真实节点
 *   （发布时快照的 nodes/connections 原样渲染，与实际节点一致，非另行生成）
 * - 顶栏：左《作品名》| 描述；右侧「当前为只读模式，如需创作请点击」
 *   + 蓝色复制项目按钮 + X 关闭
 * - 复制项目：快照 workflow_data 落库新项目 → 进入编辑器（节点/连线/分组原样可继续编辑）
 * - 无快照作品（demo/旧记录）：画布显示空态提示，复制按钮禁用
 */
export const WorkflowPreviewPage: React.FC<WorkflowPreviewPageProps> = ({ item, onClose, onCopyProject }) => {
  const [copying, setCopying] = useState(false);
  const workflow = item.workflowData;
  /* 复制成功置 true：组件卸载时 ReadOnlyCanvas cleanup 跳过清空 nodeStore（数据已由编辑器接管） */
  const suppressNodeStoreCleanup = useRef(false);

  const handleCopy = useCallback(async () => {
    if (copying || !workflow) return;
    if (!useAuthStore.getState().isLoggedIn) {
      toast.info('请先登录后再复制项目');
      return;
    }
    setCopying(true);
    try {
      const project = await useProjectStore.getState().createProject(item.title);
      await projectApi.updateProject(project.id, {
        name: item.title,
        thumbnail: item.coverUrl,
        workflow_data: {
          nodes: workflow.nodes,
          connections: workflow.connections,
          groups: workflow.groups || [],
        },
      });
      toast.success('项目已复制到我的画布');
      /* onCopyProject 已把复制项目装入编辑器，随后 onClose 卸载本组件时不得清空 nodeStore */
      suppressNodeStoreCleanup.current = true;
      await onCopyProject?.(project.id);
      onClose();
    } catch (e) {
      /* 失败时组件不卸载，重置标志让后续正常关闭仍能清空只读快照 */
      suppressNodeStoreCleanup.current = false;
      console.error('复制项目失败:', e);
      toast.error('复制项目失败，请稍后重试');
    } finally {
      setCopying(false);
    }
  }, [copying, item, workflow, onCopyProject, onClose]);

  return (
    <div className="fixed inset-0 z-[170] bg-[#141414]">
      {/* 只读画布：满铺视口，顶栏悬浮其上；有快照渲染真实节点，无快照显示空态 */}
      <div className="absolute inset-0">
        {workflow ? (
          <ReactFlowProvider>
            <ReadOnlyCanvas workflow={workflow} suppressCleanupRef={suppressNodeStoreCleanup} />
          </ReactFlowProvider>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-white/40">
            <FileBox size={40} strokeWidth={1.25} />
            <span className="text-[14px]">该作品未包含公开的制作流程数据</span>
          </div>
        )}
      </div>

      {/* 顶栏（图二）：左项目标题，右只读提示 + 复制项目 + 关闭 */}
      <div className="absolute inset-x-0 top-0 z-10 flex h-11 items-center gap-4 border-b border-white/5 bg-[#161616]/95 px-4 backdrop-blur-xl">
        <div className="min-w-0 flex-1 truncate text-[14px] text-white/90">
          《{item.title}》{item.description ? ` | ${item.description}` : ''}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-[13px] text-white/60">当前为只读模式，如需创作请点击</span>
          <button
            type="button"
            onClick={handleCopy}
            disabled={copying || !workflow}
            title={workflow ? undefined : '该作品未公开制作流程'}
            className="flex h-8 cursor-pointer items-center gap-1.5 rounded-lg bg-blue-500 px-3.5 text-[13px] font-medium text-white transition-colors hover:bg-blue-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {copying ? <Loader2 size={14} className="animate-spin" /> : null}
            {copying ? '复制中…' : '复制项目'}
          </button>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="grid h-8 w-8 cursor-pointer place-items-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
